//@flow
import * as React from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import { compose } from 'recompose';
import { translate, type TFunction } from 'react-i18next';
import { Text, Column, Columns, Flex, Box, Icon } from '@locus-taxy/neo-ui';
import classnames from 'classnames';
import _ from 'lodash';
import theme from './PolygonMapStats.scss';
import { MarkerTypeEnum, TourClusterTypeEnum } from 'common/enums/MapEnum';

type MarkerType = MarkerTypeEnum.Numbered | MarkerTypeEnum.Dot;

type OwnProps = {
    theme: ThemeType,
    showMarkers: boolean,
    markerType: MarkerType,
    showPolygonClustersFor: $Values<typeof TourClusterTypeEnum>,
    polygon: boolean,
    showUnassignedMarkers: boolean,
    currentUnallocatedCount?: number,
    onChange: (
        options: Object,
        updatedOption: { id: string, value: string | boolean }
    ) => void
};

type Props = {
    ...OwnProps,
    theme: ThemeType,
    t: TFunction,
    polygonStatsVisible: boolean,
    setPolygonStatsVisible: (visible: boolean) => void,
    mapControlVisible?: string,
    selectedOutletMetrics: {
        count: string,
        totalRevenue: string,
        totalTAT: string
    },
    color?: string
};
type State = {
    isOpen: boolean
};
class PolygonMapStats extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.onToggleView = this.onToggleView.bind(this);
    }

    onValueChange = (id: string, value: string | boolean) => {
        let { ...options } = this.props;
        const { onChange } = this.props;
        options = _.extend({}, options);
        options[id] = value;
        onChange(options);
    };

    onToggleView = () => {
        if (this.props.polygonStatsVisible) {
            this.hide();
        } else {
            this.show();
        }
    };
    show = () => {
        const { setPolygonStatsVisible } = this.props;
        setPolygonStatsVisible(true);
    };
    hide = () => {
        const { setPolygonStatsVisible } = this.props;
        setPolygonStatsVisible(false);
    };

    updateMapOptions = options => {
        this.setState(prevState => {
            const prevOptions = prevState.options;
            return {
                options: {
                    ...prevOptions,
                    ...options
                }
            };
        });
    };

    render() {
        const {
            theme,
            polygonStatsVisible,
            mapControlVisible,
            selectedOutletMetrics,
            t
        } = this.props;

        return (
            <Box
                className={classnames(theme.mapOptionsWrapRight, {
                    [theme.mapControlVisible]: mapControlVisible,
                    [theme.open]: polygonStatsVisible
                })}
            >
                <Flex
                    height={32}
                    alignItems="center"
                    pu={2}
                    pd={1}
                    onClick={this.onToggleView}
                    sx={{
                        cursor: 'pointer'
                    }}
                >
                    <Columns width="inherit">
                        <Column alignY="center" width="content">
                            <Box pt="10px" pl="8px">
                                <Icon
                                    color="white"
                                    size="xSmall"
                                    icon={
                                        polygonStatsVisible
                                            ? 'arrow_drop_down'
                                            : 'arrow_drop_right'
                                    }
                                />
                            </Box>
                        </Column>
                        <Column width="fluid">
                            <Box p={2}>
                                <Text fontSize={0} color="white">
                                    {t('createTerritory.polygonMetrics')}
                                </Text>
                            </Box>
                        </Column>
                        <Column width="content">
                            <Box p={2}>
                                {this.props.color ? (
                                    <Icon
                                        icon="circle"
                                        color={this.props.color}
                                        size="medium"
                                    />
                                ) : null}
                            </Box>
                        </Column>
                    </Columns>
                </Flex>
                <Box width="fluid" p={1} pr={2} pl={2}>
                    <Columns alignY="center" space="small">
                        <Column width="fluid">
                            <Text fontSize={0} color="white">
                                {t('createTerritory.numberOfOutlets')}
                            </Text>
                        </Column>
                        <Column width="content">
                            <Text fontSize={0} fontWeight={500} color="white">
                                {selectedOutletMetrics
                                    ? selectedOutletMetrics.count
                                    : '-'}
                            </Text>
                        </Column>
                    </Columns>
                </Box>
                <Box width="fluid" p={1} pr={2} pl={2}>
                    <Columns alignY="center" space="small">
                        <Column width="fluid">
                            <Text fontSize={0} color="white">
                                {t('createTerritory.totalRevenue')}
                            </Text>
                        </Column>
                        <Column width="content">
                            <Text fontSize={0} fontWeight={500} color="white">
                                {selectedOutletMetrics
                                    ? selectedOutletMetrics.totalRevenue
                                    : '-'}
                            </Text>
                        </Column>
                    </Columns>
                </Box>

                <Box width="fluid" p={1} pr={2} pl={2}>
                    <Columns alignY="center" space="small">
                        <Column width="fluid">
                            <Text fontSize={0} color="white">
                                {t('createTerritory.totalTransactionTime')}
                            </Text>
                        </Column>
                        <Column width="content">
                            <Text fontSize={0} fontWeight={500} color="white">
                                {selectedOutletMetrics
                                    ? selectedOutletMetrics.totalTAT
                                    : '0'}
                            </Text>
                        </Column>
                    </Columns>
                </Box>
            </Box>
        );
    }
}

export default React.memo(
    (compose(
        themr('PolygonMapStats', theme, { withRef: true }),
        translate('entityManagement', { withRef: true })
    )(PolygonMapStats): React.AbstractComponent<OwnProps>)
);
